import axios from 'axios';
import {
  Product,
  Review,
  AdminReview,
  ReviewCreate,
  ReviewModerate,
  AuthResponse,
  ReviewsResponse
} from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Интерцептор для добавления токена в каждый запрос
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Интерцептор для обработки ошибок аутентификации
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('access_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: async (username: string, password: string): Promise<AuthResponse> => {
    const formData = new FormData();
    formData.append('username', username);
    formData.append('password', password);
    
    const response = await api.post('/api/login', formData, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });
    return response.data;
  },

  logout: () => {
    localStorage.removeItem('access_token');
  },

  isAuthenticated: (): boolean => {
    return !!localStorage.getItem('access_token');
  },

  getMe: async () => {
    const response = await api.get('/api/me');
    return response.data;
  },
};

// Products API
export const productsAPI = {
  getAll: async (): Promise<Product[]> => {
    const response = await api.get('/api/products');
    return response.data;
  },

  create: async (product: Omit<Product, 'id'>): Promise<Product> => {
    const response = await api.post('/api/products', product);
    return response.data;
  },
};

// Reviews API
export const reviewsAPI = {
  getByProduct: async (
    productId: string,
    page: number = 1,
    sort: string = 'date_desc'
  ): Promise<ReviewsResponse> => {
    const response = await api.get(`/api/products/${productId}/reviews`, {
      params: { page, sort },
    });
    return response.data;
  },

  getAll: async (
    page: number = 1,
    sort: string = 'date_desc'
  ): Promise<ReviewsResponse> => {
    const response = await api.get('/api/reviews', {
      params: { page, sort },
    });
    return response.data;
  },

  create: async (reviewData: ReviewCreate): Promise<Review> => {
    const formData = new FormData();
    
    formData.append('product_id', reviewData.product_id);
    formData.append('user_email', reviewData.user_email);
    formData.append('rating', reviewData.rating.toString());
    formData.append('comment', reviewData.comment);
    
    if (reviewData.user_name) {
      formData.append('user_name', reviewData.user_name);
    }
    if (reviewData.advantages) {
      formData.append('advantages', reviewData.advantages);
    }
    if (reviewData.disadvantages) {
      formData.append('disadvantages', reviewData.disadvantages);
    }
    
    if (reviewData.photos) {
      reviewData.photos.forEach((photo) => {
        formData.append('photos', photo);
      });
    }

    const response = await api.post('/api/reviews', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
};

// Admin API
export const adminAPI = {
  getReviews: async (
    status: string = 'pending',
    page: number = 1,
    perPage: number = 10
  ): Promise<AdminReview[]> => {
    const response = await api.get('/api/admin/reviews', {
      params: { status, page, per_page: perPage },
    });
    return response.data;
  },

  moderateReview: async (
    reviewId: string,
    moderation: ReviewModerate
  ): Promise<{ status: string; review_id: string; new_status: string }> => {
    const response = await api.put(`/api/admin/reviews/${reviewId}/moderate`, moderation);
    return response.data;
  },

  updateReview: async (
    reviewId: string,
    reviewData: any
  ): Promise<{ status: string; message: string }> => {
    const response = await api.put(`/api/admin/reviews/${reviewId}`, reviewData);
    return response.data;
  },

  deletePhoto: async (
    reviewId: string,
    photoId: string
  ): Promise<{ status: string; message: string }> => {
    const response = await api.delete(`/api/admin/reviews/${reviewId}/photos/${photoId}`);
    return response.data;
  },

  deleteReview: async (
    reviewId: string
  ): Promise<{ status: string; message: string; review_id?: string }> => {
    const response = await api.delete(`/api/admin/reviews/${reviewId}`);
    return response.data;
  },
};

export default api;


